/**
 * Runtime sticky-note text fitting (needs Excalidraw's text measurement, which
 * `restoreElements(…, {refreshDimensions: true})` exposes: it re-wraps a bound
 * text to its container's width and measures it).
 *
 * After the user edits or resizes a sticky locally (never for remote changes
 * — two browsers measuring slightly differently must not ping-pong), the note
 * is squared to its width and its text gets the largest font size that fits.
 */

import { CaptureUpdateAction, newElementWith, restoreElements } from '@excalidraw/excalidraw';
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';
import { centreIn, type El } from './elements';
import { isStickyContainer, pickStickyFontSize, STICKY_FONT_SIZES, STICKY_PADDING } from './sticky';

function boundTextId(container: El): string | null {
  const b = (container.boundElements ?? []) as { id: string; type: string }[];
  return b.find((x) => x.type === 'text')?.id ?? null;
}

/** Measure `text` bound in `container` at `fontSize` (Excalidraw's own wrap + metrics). */
export function measureBound(container: El, text: El, fontSize: number): El {
  const c = { ...container };
  const t = { ...text, fontSize, text: text.originalText ?? text.text };
  const restored = restoreElements([c, t] as never, null, { refreshDimensions: true, repairBindings: true }) as unknown as El[];
  return restored.find((e) => e.id === text.id) ?? t;
}

export interface FitResult {
  container: Partial<El> | null;
  text: Partial<El> | null;
}

/** What has to change for this sticky to be square with fitted, centred text (null = nothing). */
export function computeFit(container: El, text: El | null): FitResult | null {
  const size = Math.max(40, Math.round(container.width));
  const sq = { ...container, height: size, width: size };
  const containerPatch = Math.abs(container.height - size) > 0.5 || Math.abs(container.width - size) > 0.5 ? { height: size, width: size } : null;
  if (!text || text.isDeleted) return containerPatch ? { container: containerPatch, text: null } : null;
  const cache = new Map<number, El>();
  const measure = (fs: number) => {
    let m = cache.get(fs);
    if (!m) cache.set(fs, (m = measureBound(sq, text, fs)));
    return { width: m.width as number, height: m.height as number };
  };
  const fontSize = pickStickyFontSize(measure, size, STICKY_FONT_SIZES);
  measure(fontSize);
  const m = cache.get(fontSize)!;
  const placed = centreIn({ ...text, width: m.width, height: m.height }, sq);
  const textPatch: Partial<El> = {};
  if (text.fontSize !== fontSize) textPatch.fontSize = fontSize;
  if (text.text !== m.text) textPatch.text = m.text;
  if (Math.abs(text.width - m.width) > 0.5) textPatch.width = m.width;
  if (Math.abs(text.height - m.height) > 0.5) textPatch.height = m.height;
  if (Math.abs(text.x - placed.x) > 0.5) textPatch.x = placed.x;
  if (Math.abs(text.y - placed.y) > 0.5) textPatch.y = placed.y;
  if (text.verticalAlign !== 'middle') textPatch.verticalAlign = 'middle';
  if (text.textAlign !== 'center') textPatch.textAlign = 'center';
  const hasText = Object.keys(textPatch).length > 0;
  if (!containerPatch && !hasText) return null;
  return { container: containerPatch, text: hasText ? textPatch : null };
}

/** Apply fits to a plain element list WITHOUT bumping versions (template seeding). */
export function fitAllStatic<T extends El>(elements: T[]): T[] {
  const byId = new Map(elements.map((e) => [e.id, e]));
  for (const c of elements) {
    if (!isStickyContainer(c)) continue;
    const tid = boundTextId(c);
    const t = tid ? byId.get(tid) ?? null : null;
    const fit = computeFit(c, t);
    if (!fit) continue;
    if (fit.container) byId.set(c.id, { ...c, ...fit.container } as T);
    if (fit.text && t) byId.set(t.id, { ...t, ...fit.text } as T);
  }
  return elements.map((e) => byId.get(e.id)!);
}

/** Excalidraw font family 6 — served as Karla, the brand font (web/shared/vite.fonts.ts). */
const BRAND_FAMILY = 6;
/** Padding Excalidraw keeps inside a non-sticky container. */
const BOUND_PADDING = 5;

/**
 * One-time re-fit of text in the brand family after the switch from Nunito to Karla (Karla Regular is
 * ~1% wider on average, up to ~5% on some lines, so stored line breaks can overflow). Each brand-family
 * text is re-wrapped in its container at its own size, stepping the size down only if it no longer fits;
 * loose text gets fresh dimensions. Marked `customData.brandFit = 1` so it runs once per element, by
 * whichever editing client opens the board first (the result is deterministic, so a race is harmless).
 * Containers keep their size — imported Miro stickies aren't squared. Returns # elements changed.
 */
export async function refitBrandText(xapi: ExcalidrawImperativeAPI): Promise<number> {
  const pending = () => (xapi.getSceneElements() as unknown as El[]).some((t) => t.type === 'text' && t.fontFamily === BRAND_FAMILY && !(t.customData as { brandFit?: number } | undefined)?.brandFit);
  if (!pending()) return 0;
  // Measure with the real face only: Excalidraw adds its "Nunito" (= Karla) faces once a scene uses them.
  const loaded = () => [...document.fonts].some((f) => f.family.replace(/["']/g, '') === 'Nunito' && f.status === 'loaded');
  for (let i = 0; i < 50 && !loaded(); i++) {
    try {
      await document.fonts.load('20px Nunito', 'Ag');
    } catch {
      /* retry */
    }
    if (!loaded()) await new Promise((r) => setTimeout(r, 100));
  }
  if (!loaded()) return 0; // not yet: leave unmarked, a later visit fits it
  const els = xapi.getSceneElementsIncludingDeleted() as unknown as El[];
  const byId = new Map(els.map((e) => [e.id, e]));
  const updates = new Map<string, El>();
  for (const t of els) {
    if (t.type !== 'text' || t.isDeleted || t.fontFamily !== BRAND_FAMILY || (t.customData as { brandFit?: number } | undefined)?.brandFit) continue;
    const customData = { ...(t.customData ?? {}), brandFit: 1 };
    const c = t.containerId ? byId.get(t.containerId) : null;
    if (c && !c.isDeleted && c.type !== 'arrow' && c.type !== 'line') {
      const pad = isStickyContainer(c) ? STICKY_PADDING : BOUND_PADDING;
      const room = Math.max(1, c.height - 2 * pad);
      let fontSize = t.fontSize as number;
      let m = measureBound(c, t, fontSize);
      for (const s of STICKY_FONT_SIZES.filter((x) => x < fontSize)) {
        if ((m.height as number) <= room) break;
        fontSize = s;
        m = measureBound(c, t, fontSize);
      }
      const sized = { ...t, text: m.text, fontSize, width: m.width as number, height: m.height as number };
      const placed = t.verticalAlign === 'top' ? { ...sized, y: t.y } : centreIn(sized as El, c);
      updates.set(t.id, newElementWith(t as never, { text: m.text, fontSize, width: sized.width, height: sized.height, x: placed.x, y: placed.y, customData } as never) as unknown as El);
    } else if (!t.containerId) {
      const [r] = restoreElements([{ ...t, text: t.originalText ?? t.text }] as never, null, { refreshDimensions: true }) as unknown as El[];
      updates.set(t.id, newElementWith(t as never, { width: r.width, height: r.height, customData } as never) as unknown as El);
    }
  }
  if (!updates.size) return 0;
  xapi.updateScene({ elements: els.map((e) => updates.get(e.id) ?? e) as never, captureUpdate: CaptureUpdateAction.NEVER });
  return updates.size;
}

export class StickyFitter {
  private remote = new Map<string, number>();
  private checked = new Map<string, number>();
  pointerDown = false;

  constructor(private readonly xapi: ExcalidrawImperativeAPI) {}

  /** Elements applied from the server — never fitted by this client. */
  noteRemote(els: readonly { id: string; version: number }[]): void {
    for (const e of els) this.remote.set(e.id, e.version);
  }

  /** Fit locally edited stickies once editing / dragging has finished. Returns # changed. */
  run(): number {
    const st = this.xapi.getAppState();
    if (this.pointerDown || st.editingTextElement || st.newElement || st.resizingElement || st.selectedElementsAreBeingDragged) return 0;
    const els = this.xapi.getSceneElementsIncludingDeleted() as unknown as El[];
    const byId = new Map(els.map((e) => [e.id, e]));
    const updates = new Map<string, El>();
    for (const c of els) {
      if (c.isDeleted || !isStickyContainer(c)) continue;
      const tid = boundTextId(c);
      const t = tid ? byId.get(tid) ?? null : null;
      const parts = [c, t].filter(Boolean) as El[];
      const dirty = parts.some((e) => this.remote.get(e.id) !== e.version && this.checked.get(e.id) !== e.version);
      if (!dirty) continue;
      for (const e of parts) this.checked.set(e.id, e.version);
      const fit = computeFit(c, t);
      if (!fit) continue;
      if (fit.container) {
        const nc = newElementWith(c as never, fit.container as never) as unknown as El;
        updates.set(c.id, nc);
        this.checked.set(c.id, nc.version);
      }
      if (fit.text && t) {
        const nt = newElementWith(t as never, fit.text as never) as unknown as El;
        updates.set(t.id, nt);
        this.checked.set(t.id, nt.version);
      }
    }
    if (!updates.size) return 0;
    this.xapi.updateScene({ elements: els.map((e) => updates.get(e.id) ?? e) as never, captureUpdate: CaptureUpdateAction.NEVER });
    return updates.size;
  }
}
